import type { ReactNode } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import type { PaginationMeta, SortDirection } from '@amai/contracts';
import { Button } from './Button';

export interface Column<T> {
  key: string;
  label: string;
  /** Alinha à direita e usa fonte tabular — para valores e quantidades. */
  right?: boolean;
  mono?: boolean;
  strong?: boolean;
  sortable?: boolean;
  width?: number | string;
  render: (row: T) => ReactNode;
}

export interface SortState {
  sort?: string;
  direction: SortDirection;
}

/**
 * SmartDataTable — a tabela do AMAI OPS.
 *
 * Linha de 44px, cabeçalho em fundo suave, clique na linha abre o painel
 * lateral (nunca uma navegação que faz o operador perder a lista). As ações
 * da linha aparecem com o mouse ou o foco de teclado sobre ela.
 */
export function SmartDataTable<T extends { id: string }>({
  columns,
  rows,
  meta,
  selectedId,
  onRowClick,
  onPageChange,
  sortState,
  onSortChange,
  emptyState,
  caption,
  checkedIds,
  onCheckedChange,
  rowActions,
  perPage,
  onPerPageChange,
}: {
  columns: Array<Column<T>>;
  rows: T[];
  meta?: PaginationMeta;
  selectedId?: string | null;
  onRowClick?: (row: T) => void;
  onPageChange?: (page: number) => void;
  sortState?: SortState;
  onSortChange?: (state: SortState) => void;
  emptyState?: ReactNode;
  caption: string;
  /** Seleção múltipla. Só aparece quando o chamador passa os dois. */
  checkedIds?: string[];
  onCheckedChange?: (ids: string[]) => void;
  /** Coluna de ações à direita, fora do clique que abre a linha. */
  rowActions?: (row: T) => ReactNode;
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
}) {
  if (rows.length === 0 && emptyState) return <>{emptyState}</>;

  const selecionavel = Boolean(checkedIds && onCheckedChange);
  const marcados = new Set(checkedIds ?? []);
  // "Todas" é sempre das linhas VISÍVEIS. Marcar a caixa do cabeçalho e levar
  // junto as outras páginas seria o tipo de surpresa que só se descobre depois
  // de cancelar oitenta requisições.
  const todasNaPagina = rows.length > 0 && rows.every((row) => marcados.has(row.id));

  const alternar = (id: string) => {
    if (!onCheckedChange) return;
    const proximo = new Set(marcados);
    if (proximo.has(id)) proximo.delete(id);
    else proximo.add(id);
    onCheckedChange([...proximo]);
  };

  const alternarPagina = () => {
    if (!onCheckedChange) return;
    const proximo = new Set(marcados);
    for (const row of rows) {
      if (todasNaPagina) proximo.delete(row.id);
      else proximo.add(row.id);
    }
    onCheckedChange([...proximo]);
  };

  const toggleSort = (key: string) => {
    if (!onSortChange) return;
    const same = sortState?.sort === key;
    onSortChange({
      sort: key,
      direction: same && sortState?.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  return (
    <div className="table-wrap">
      <div className="table-scroll">
        <table className="table">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {selecionavel && (
                <th className="table__check">
                  <input
                    type="checkbox"
                    checked={todasNaPagina}
                    onChange={alternarPagina}
                    aria-label="Selecionar todas as linhas desta página"
                  />
                </th>
              )}
              {columns.map((column) => {
                const active = sortState?.sort === column.key;
                return (
                  <th
                    key={column.key}
                    className={[column.right ? 'is-right' : '', column.sortable ? 'is-sortable' : '']
                      .filter(Boolean)
                      .join(' ')}
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={
                      active ? (sortState?.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                    }
                    onClick={column.sortable ? () => toggleSort(column.key) : undefined}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {column.label}
                      {active &&
                        (sortState?.direction === 'asc' ? (
                          <ChevronUp size={12} aria-hidden />
                        ) : (
                          <ChevronDown size={12} aria-hidden />
                        ))}
                    </span>
                  </th>
                );
              })}
              {rowActions && (
                <th className="is-right table__actions" style={{ width: 56 }}>
                  <span className="sr-only">Ações</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={[
                  onRowClick ? 'is-clickable' : '',
                  selectedId === row.id ? 'is-selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {selecionavel && (
                  <td className="table__check" onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={marcados.has(row.id)}
                      onChange={() => alternar(row.id)}
                      aria-label="Selecionar esta linha"
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={[
                      column.right ? 'is-right' : '',
                      column.strong ? 'is-strong' : '',
                      column.mono ? 'mono' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {column.render(row)}
                  </td>
                ))}
                {rowActions && (
                  <td className="is-right table__actions" onClick={(event) => event.stopPropagation()}>
                    <span className="row-actions">{rowActions(row)}</span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {meta && onPageChange && meta.totalPages > 0 && (
        <div className="table-footer">
          <span>
            {meta.total === 1 ? '1 registro' : `${meta.total.toLocaleString('pt-BR')} registros`}
            {meta.totalPages > 1 && ` · página ${meta.page} de ${meta.totalPages}`}
            {marcados.size > 0 && ` · ${marcados.size} selecionada(s)`}
          </span>
          <div className="table-footer__pages">
            {perPage !== undefined && onPerPageChange && (
              <select
                className="filterbar__select"
                value={perPage}
                onChange={(event) => onPerPageChange(Number(event.target.value))}
                aria-label="Registros por página"
              >
                {[10, 25, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value} por página
                  </option>
                ))}
              </select>
            )}
            {meta.totalPages > 1 && (
              <>
                <Button
                  small
                  variant="outline"
                  icon={ChevronLeft}
                  disabled={meta.page <= 1}
                  onClick={() => onPageChange(meta.page - 1)}
                >
                  Anterior
                </Button>
                <Button
                  small
                  variant="outline"
                  disabled={meta.page >= meta.totalPages}
                  onClick={() => onPageChange(meta.page + 1)}
                >
                  Próxima <ChevronRight size={13} aria-hidden />
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
